import { useState, useEffect, useRef, useCallback } from "react";
import { copyToClipboard } from "@cede/utils";
import * as Sentry from "@sentry/nextjs";
import { enqueueSnackbar } from "notistack";

/**
 * Hook for clipboard operations (copy and paste) with state management
 * @param timeout - Time in milliseconds before resetting the copied state (default: 2000ms)
 * @returns Object with clipboard utilities
 */
export const useClipboard = (timeout = 2000) => {
  const [isCopied, setIsCopied] = useState(false);
  const resetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (resetTimeoutRef.current) {
        clearTimeout(resetTimeoutRef.current);
      }
    };
  }, []);

  /**
   * Copy text to clipboard
   * @param text - Text to copy
   */
  const handleCopy = async (text: string) => {
    if (!text) {
      return;
    }

    try {
      await copyToClipboard(text);
      setIsCopied(true);

      // Clear any existing timeout
      if (resetTimeoutRef.current) {
        clearTimeout(resetTimeoutRef.current);
      }

      // Reset the copied state after the specified timeout
      resetTimeoutRef.current = setTimeout(() => {
        setIsCopied(false);
        resetTimeoutRef.current = null;
      }, timeout);
    } catch (error) {
      enqueueSnackbar("Couldn't copy to clipboard. Your browser or wallet may have blocked it.", { variant: "error" });
      Sentry.captureException(error, {
        tags: {
          context: "copy_to_clipboard",
        },
        extra: {
          textLength: text?.length,
        },
      });
    }
  };

  /**
   * Create a paste handler with custom processing logic
   * @param onPaste - Callback to process pasted text
   * @returns Paste event handler
   */
  const createPasteHandler = useCallback((onPaste: (text: string) => void) => {
    return (e: React.ClipboardEvent<HTMLInputElement>) => {
      try {
        e.preventDefault();
        const pastedText = e.clipboardData.getData("text");
        onPaste(pastedText);
      } catch (error) {
        Sentry.captureException(error, {
          tags: {
            context: "paste_from_clipboard",
          },
          extra: {
            hasClipboardData: !!e.clipboardData,
          },
        });
      }
    };
  }, []);

  return { isCopied, handleCopy, createPasteHandler };
};
